/**
 * Permission registry — the single source of truth for `module.action` permissions.
 * Backend enforces via requirePermission(); UIs gate via useCan().
 */

export interface PermissionDef {
  key: string;
  module: string;
  action: string;
  label: string;
}

function group(module: string, moduleLabel: string, actions: Record<string, string>): PermissionDef[] {
  return Object.entries(actions).map(([action, label]) => ({
    key: `${module}.${action}`,
    module,
    action,
    label: `${moduleLabel}: ${label}`,
  }));
}

export const PERMISSIONS: PermissionDef[] = [
  ...group("dashboard", "Dashboard", { view: "View dashboards" }),
  ...group("users", "Users", {
    read: "View users",
    write: "Create and edit users",
    suspend: "Suspend / ban users",
    adjust_credit: "Manually adjust wallet credit",
  }),
  ...group("rbac", "RBAC", {
    manage: "Manage roles and permissions",
    modules: "Toggle feature modules",
  }),
  ...group("settings", "Settings", {
    read: "View settings",
    write: "Edit settings (SMTP, gateways, timers, currencies)",
  }),
  ...group("catalog", "Catalog", {
    read: "View catalog",
    write: "Create and edit categories / products",
    stock: "Adjust stock and run audits",
  }),
  ...group("discounts", "Discounts", { read: "View discounts", write: "Manage discount rules" }),
  ...group("orders", "Orders", {
    read: "View orders",
    write: "Edit / cancel orders",
    fulfil: "Pick, pack and ship orders",
    refund: "Issue refunds",
  }),
  ...group("payments", "Payments", {
    read: "View payments",
    verify_receipt: "Verify manual bank-in receipts",
  }),
  ...group("raffles", "Raffles", {
    read: "View raffles",
    write: "Create and edit raffles",
    draw: "Trigger / confirm raffle draws",
    monitor: "Monitor live raffle rooms",
  }),
  ...group("auctions", "Auctions", {
    read: "View auctions",
    write: "Create and edit auctions",
    manage: "Cancel auctions / remove bids",
  }),
  ...group("packs", "Packs", {
    read: "View packs",
    write: "Create and edit packs / odds",
  }),
  ...group("vault", "Vault", { manage: "Manage user vaults and shipment requests" }),
  ...group("trading", "Trading", { manage: "Moderate P2P trades" }),
  ...group("wallet", "Wallet", { read: "View wallets and transactions" }),
  ...group("ai", "AI", {
    configure: "Configure AI models and knowledge base",
    copilot: "Use backoffice AI copilot",
    cases: "Manage AI cases / tickets",
    forms: "Manage dynamic forms",
  }),
  ...group("carduploader", "Card Uploader", {
    use: "Upload and identify cards",
    publish: "Publish identified cards to catalog",
  }),
  ...group("notifications", "Notifications", { announce: "Send announcements" }),
  ...group("reports", "Reports", { view: "View reports and exports" }),
  ...group("documents", "Documents", {
    read: "View invoice/receipt templates and order PDFs",
    write: "Edit templates and company invoicing settings",
  }),
  ...group("cms", "CMS", {
    read: "View CMS pages and hero slides",
    write: "Create and edit CMS pages and hero carousel",
  }),
  ...group("marketplace", "Marketplace", {
    manage: "Manage marketplace settings and sellers",
    verify_kyc: "Review and approve seller KYC",
    moderate_listings: "Moderate marketplace listings",
    read: "View marketplace orders and settlements",
  }),
  ...group("flexxi", "FlexxiPlan", {
    read: "View FlexxiPlan contracts and customer schedules",
    manage_templates: "Manage instalment plan templates",
    manage_plans: "Adjust plans, waive fees, record offline payments",
    resolve_default: "Resolve defaulted plan asset disposition",
    verify_kyc: "Review and approve FlexxiPlan customer identity verification",
  }),
];

export const PERMISSION_KEYS = PERMISSIONS.map((p) => p.key);
export type PermissionKey = string;

/** Role name given implicit access to everything. */
export const SUPERADMIN_ROLE = "superadmin";

export const SYSTEM_ROLES: { name: string; label: string; permissions: string[] | "*" }[] = [
  { name: "superadmin", label: "Super Admin", permissions: "*" },
  {
    name: "admin",
    label: "Admin",
    permissions: PERMISSION_KEYS.filter((k) => !k.startsWith("rbac.")),
  },
  {
    name: "staff",
    label: "Staff",
    permissions: [
      "dashboard.view",
      "catalog.read",
      "orders.read",
      "orders.fulfil",
      "payments.read",
      "payments.verify_receipt",
      "raffles.read",
      "raffles.monitor",
      "auctions.read",
      "packs.read",
      "carduploader.use",
      "documents.read",
      "cms.read",
    ],
  },
  { name: "customer", label: "Customer", permissions: [] },
];
